import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from "@angular/core";
import { TacticType } from "@front/m19-api-client";
import { StrategyService } from "@front/m19-injection-token";
import { AdvertisingStrategyEx, SegmentConfigType, SegmentEx, StrategyEx } from "@front/m19-models";
import { IToggleComponent, ToasterService } from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { SettingRowComponent } from "@m19-board/shared/strategy-advanced-settings/setting-row/setting-row.component";
import { DisableAutoWarningComponent } from "@m19-board/strategies/disable-auto/disable-auto-warning.component";

@Component({
  selector: "app-strategy-page-toggle-ai-powered-targeting",
  imports: [DisableAutoWarningComponent, IToggleComponent, TranslocoDirective, SettingRowComponent],
  template: ` <ng-container *transloco="let t">
    @if (advertisingStrategy(); as strategy) {
      <app-setting-row>
        <ng-container label>
          @if (!strategy.autoAlgoExplorationEnabled) {
            <app-disable-auto-warning [campaignType]="strategy.campaignType" />
          }
          {{ t("advanced-settings-modal.ai-powered_targeting") }}
        </ng-container>
        <IToggle
          testid="ai-targeting"
          [loading]="isLoading()"
          [disabled]="isReadOnly() || isAITargetingDisabled()"
          [checked]="strategy.autoAlgoExplorationEnabled"
          (checkedChange)="switchAutoAlgoExploration()"
        />
      </app-setting-row>
      <p class="w-0 min-w-full text-sm text-red-500">
        {{ t("advanced-settings-modal.disabled_warning") }}
      </p>
    }
  </ng-container>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyPageToggleAiPoweredTargetingComponent {
  private readonly strategyService = inject(StrategyService);
  private readonly toasterService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);

  strategy = input.required<StrategyEx>();
  organizationId = input.required<number>();
  isReadOnly = input.required<boolean>();
  segmentIndex = input.required<Map<number, SegmentEx>>();

  advertisingStrategy = computed(() => {
    const strategy = this.strategy();
    return strategy.isAdvertisingStrategy() ? strategy : undefined;
  });

  isAITargetingDisabled = computed(() => {
    const strategy = this.advertisingStrategy();
    if (!strategy) return true;

    return (
      strategy.autoAlgoExplorationEnabled &&
      (!strategy.isSpStrategy() || !strategy.productTargetingEnabled) &&
      strategy.tactics.length > 0 &&
      strategy.tactics.every((t) => {
        const segment = this.segmentIndex()?.get(t.segmentId);
        return segment?.segmentType == SegmentConfigType.ProductSegment || t.tacticType == TacticType.BLACKLIST;
      })
    );
  });

  isLoading = signal(false);

  switchAutoAlgoExploration() {
    const strategy = this.advertisingStrategy();
    if (!strategy) return;
    this.isLoading.set(true);
    this.strategyService
      .updateStrategyAutoAlgoExploration(
        strategy.accountId,
        strategy.marketplace,
        this.organizationId()!,
        strategy.strategyId,
        !strategy.autoAlgoExplorationEnabled,
      )
      .subscribe({
        next: (s) => {
          const updatedStrategy = s as AdvertisingStrategyEx;
          this.toasterService.success(
            updatedStrategy.autoAlgoExplorationEnabled
              ? this.translocoService.translate("strategy-page.ai-powered_targeting_enabled")
              : this.translocoService.translate("strategy-page.ai-powered_targeting_disabled"),
            this.translocoService.translate("advanced-settings-modal.ai-powered_targeting"),
          );
          this.isLoading.set(false);
        },
        error: (error) => {
          this.toasterService.error(error, this.translocoService.translate("strategy-page.ai-powered_targeting_error"));
          this.isLoading.set(false);
        },
      });
  }
}
